import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, X, Share, PlusSquare, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const DISMISS_KEY = "pdv_install_dismissed_at";
const DISMISS_DAYS = 3;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function getDeviceId() {
  let id = localStorage.getItem("pdv_device_id");
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("pdv_device_id", id);
  }
  return id;
}

export function trackInstallEvent(event, extra = {}) {
  const displayMode = window.matchMedia?.("(display-mode: standalone)")?.matches ? "standalone" : (window.navigator?.standalone ? "ios-standalone" : "browser");
  return axios.post(`${API}/pwa/install-event`, {
    event, device_id: getDeviceId(), user_agent: navigator.userAgent, display_mode: displayMode, ...extra,
  }).catch(() => {});
}

export const isStandalone = () =>
  (typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)")?.matches) || window.navigator?.standalone === true;

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

let deferredPrompt = null;
const listeners = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    listeners.forEach((fn) => fn(true));
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    listeners.forEach((fn) => fn(false));
    trackInstallEvent("installed");
    toast.success("FUTURO PDV instalado! Abra pelo ícone na tela inicial.");
  });
  // Count launches from the installed app (one per device/day) — also the only install signal on iOS
  if (isStandalone() || new URLSearchParams(window.location.search).get("source") === "pwa") {
    const key = "pdv_standalone_launch_day";
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(key) !== today) {
      localStorage.setItem(key, today);
      setTimeout(() => trackInstallEvent("launched_standalone", { source: new URLSearchParams(window.location.search).get("source") }), 1500);
    }
  }
}

/** Shared hook: whether a native install prompt is available (Chrome/Edge/Android) or iOS manual steps apply. */
export function useInstallPrompt() {
  const [canInstall, setCanInstall] = useState(!!deferredPrompt);
  const [iosOpen, setIosOpen] = useState(false);
  useEffect(() => {
    const fn = (v) => setCanInstall(v);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  const install = async () => {
    if (deferredPrompt) {
      trackInstallEvent("prompt_shown");
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice.catch(() => ({ outcome: "dismissed" }));
      trackInstallEvent(outcome === "accepted" ? "accepted" : "dismissed");
      if (outcome === "accepted") { deferredPrompt = null; setCanInstall(false); }
      return outcome;
    }
    if (isIOS()) { setIosOpen(true); return "ios"; }
    toast.info("Use o menu do navegador → “Instalar app” ou “Adicionar à tela inicial”.");
    return "manual";
  };
  const available = !isStandalone() && (canInstall || isIOS());
  return { available, canInstall, install, iosOpen, setIosOpen };
}

export function IOSInstallDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="ios-install-dialog">
        <DialogHeader>
          <DialogTitle>Instalar no iPhone/iPad</DialogTitle>
          <DialogDescription>O Safari não mostra o botão automático — siga estes passos:</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 text-sm">
          <li className="flex items-center gap-3"><span className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">1</span> Toque em <Share className="w-4 h-4 inline" /> <b>Compartilhar</b> na barra do Safari</li>
          <li className="flex items-center gap-3"><span className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">2</span> Escolha <PlusSquare className="w-4 h-4 inline" /> <b>Adicionar à Tela de Início</b></li>
          <li className="flex items-center gap-3"><span className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">3</span> Confirme em <b>Adicionar</b> — o app abre em tela cheia</li>
        </ol>
      </DialogContent>
    </Dialog>
  );
}

/** Slim top banner offering installation (auto-hidden when already installed or recently dismissed). */
export default function InstallPrompt() {
  const { available, install, iosOpen, setIosOpen } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(() => {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return at && Date.now() - at < DISMISS_DAYS * 86400000;
  });
  if (!available || dismissed) return <IOSInstallDialog open={iosOpen} onOpenChange={setIosOpen} />;
  const dismiss = () => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setDismissed(true); };
  return (
    <>
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 text-white" data-testid="install-banner">
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-3 text-sm">
          <Smartphone className="w-4 h-4 shrink-0" />
          <span className="flex-1 truncate"><b>Instale o FUTURO PDV</b> — abre em tela cheia como um app, com notificações.</span>
          <Button size="sm" variant="secondary" className="h-7 rounded-full" onClick={install} data-testid="install-banner-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Instalar
          </Button>
          <button onClick={dismiss} aria-label="Fechar" className="opacity-80 hover:opacity-100" data-testid="install-banner-close"><X className="w-4 h-4" /></button>
        </div>
      </div>
      <IOSInstallDialog open={iosOpen} onOpenChange={setIosOpen} />
    </>
  );
}
